import React, { useMemo, useState, Children } from 'react';
import { ChevronLeft, Sparkles, FolderOpen, X, User, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../components/LanguageContext';
import { TOPICS } from '../data/topics';
import { MOCK_PROFILES, MockProfile } from '../data/mockProfiles';
import { ProfileRow } from '../components/ProfileRow';
import { ProfileDetail } from '../components/ProfileDetail';
import { TOPIC_IMAGES } from '../components/TopicImageCard';
const SPEAKER_PHOTOS = ['https://cdn.magicpatterns.com/uploads/bw2DnyM6FmhKq8Tom4A8PK/Screenshot_2026-04-22_alle_14.08.09.png', 'https://cdn.magicpatterns.com/uploads/aXm9zUaw7Zgc93hDNUKHT4/Screenshot_2026-04-22_alle_14.07.54.png', 'https://cdn.magicpatterns.com/uploads/4d3qW4vZHbhTCP7TFhFLea/Screenshot_2026-04-22_alle_14.07.40.png', 'https://cdn.magicpatterns.com/uploads/bEsp2sbrpANdQoJZXBAUh9/Screenshot_2026-04-22_alle_14.07.34.png', 'https://cdn.magicpatterns.com/uploads/a2y8aUgNhv3KufdXF2MAs6/Screenshot_2026-04-22_alle_14.07.27.png'];
type EventType = 'KEYNOTE' | 'PANEL' | 'RESEARCH' | 'WORKSHOP' | 'IDEATHON' | 'MUSIC' | 'BREAK' | 'CLOSING' | 'OPENING';
type TbcStatus = 'tbc' | 'tbd' | null;
interface Speaker {
  name: string;
  role: string;
  tbc?: TbcStatus;
}
interface Event {
  id: string;
  time: string;
  title: string;
  type: EventType;
  speakers?: Speaker[];
  moderator?: string;
  moderatorTbc?: TbcStatus;
  performer?: string;
}
function stripTypePrefix(title: string): string {
  return title.replace(/^(KEYNOTE|Panel\s+\d+(?:\s*\[[^\]]+\])?|Research|Workshop|Ideathon|Closing Remarks)\s*:\s*/i, '');
}
const getBadgeStyles = (type: EventType) => {
  if (type === 'BREAK') {
    return 'bg-[rgba(197,216,45,0.08)] text-[#888888]';
  }
  return 'bg-[rgba(197,216,45,0.15)] text-[#C5D82D]';
};
function TbcPill({
  tbc
}: {
  tbc: TbcStatus;
}) {
  const {
    t
  } = useLanguage();
  if (!tbc) return null;
  const label = tbc === 'tbc' ? t('schedule.tbc') : t('schedule.tbd');
  return <span className="ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-[#2A2A2A] text-[#AAAAAA] align-middle" title={label} data-id="element-953">
      {tbc.toUpperCase()}
    </span>;
}
function TopicEventRow({
  event
}: {
  event: Event;
}) {
  const {
    t
  } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasParticipants = event.speakers && event.speakers.length > 0 || event.moderator;
  const isSpecialRow = event.type === 'BREAK' || event.type === 'MUSIC';
  const participantCount = (event.speakers?.length || 0) + (event.moderator ? 1 : 0);
  const participantsLabel = participantCount === 1 ? t('schedule.participant', {
    n: participantCount
  }) : t('schedule.participants', {
    n: participantCount
  });
  return <div className="bg-[#141414] border border-[#2A2A2A] rounded-[12px] mb-2 overflow-hidden" data-id="element-954">
      <button onClick={() => hasParticipants && !isSpecialRow && setIsExpanded(!isExpanded)} className="w-full flex gap-3 py-3 px-4 text-left hover:bg-[#1A1A1A] transition-colors" disabled={!hasParticipants || isSpecialRow} data-id="element-955">
        {/* Time */}
        <div className="w-12 shrink-0 text-[13px] text-[#888888] tabular-nums mt-0.5" data-id="element-956">
          {event.time}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-957">
          <h3 className={`text-[14px] font-medium leading-snug ${isSpecialRow ? 'text-[#888888] italic' : 'text-white'}`} data-id="element-958">
            {stripTypePrefix(event.title)}
          </h3>
          {event.performer && <p className="text-[12px] text-[#888888] mt-1" data-id="element-959">{event.performer}</p>}
          {hasParticipants && !isSpecialRow && <div className="flex items-center gap-1.5 mt-1.5" data-id="element-960">
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-[#555555]" data-id="element-961" /> : <ChevronDown className="w-3.5 h-3.5 text-[#555555]" data-id="element-962" />}
              <span className="text-[12px] text-[#555555]" data-id="element-963">
                {participantsLabel}
              </span>
            </div>}
        </div>

        {/* Badge */}
        <div className={`rounded-full px-2 py-0.5 text-[9px] uppercase font-semibold whitespace-nowrap self-start ${getBadgeStyles(event.type)}`} data-id="element-964">
          {event.type}
        </div>
      </button>

      {/* Expanded Content */}
      <AnimatePresence data-id="element-965">
        {isExpanded && hasParticipants && !isSpecialRow && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="overflow-hidden bg-[#1A1A1A] border-t border-[#2A2A2A]" data-id="element-966">
            <div className="px-4 py-3" data-id="element-967">
              {/* Speakers */}
              {event.speakers?.map((speaker, index) => <div key={index} className={`flex gap-3 py-2 ${index < (event.speakers?.length || 0) - 1 || event.moderator ? 'border-b border-[#2A2A2A]' : ''}`} data-id="element-968">
                  <img src={SPEAKER_PHOTOS[index % SPEAKER_PHOTOS.length]} alt={speaker.name} className="w-8 h-8 shrink-0 rounded-full object-cover" data-id="element-969" />
                  <div className="flex-1 min-w-0" data-id="element-970">
                    <p className="text-[13px] font-medium text-white" data-id="element-971">
                      {speaker.name}
                      <TbcPill tbc={speaker.tbc ?? null} data-id="element-972" />
                    </p>
                    {speaker.role && <p className="text-[11px] text-[#888888] mt-0.5 leading-snug" data-id="element-973">
                        {speaker.role}
                      </p>}
                  </div>
                </div>)}

              {/* Moderator */}
              {event.moderator && <div className="pt-2" data-id="element-974">
                  <div className="text-[9px] tracking-wider text-[#C5D82D] mb-1 font-semibold uppercase" data-id="element-975">
                    {t('schedule.moderator')}
                  </div>
                  <div className="flex gap-3 py-2" data-id="element-976">
                    <img src={SPEAKER_PHOTOS[(event.speakers?.length || 0) % SPEAKER_PHOTOS.length]} alt={event.moderator} className="w-8 h-8 shrink-0 rounded-full object-cover" data-id="element-977" />
                    <div className="flex-1 min-w-0" data-id="element-978">
                      <p className="text-[13px] font-medium text-white" data-id="element-979">
                        {event.moderator}
                        <TbcPill tbc={event.moderatorTbc ?? null} data-id="element-980" />
                      </p>
                    </div>
                  </div>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const TOPIC_RELATED_EVENTS: Record<string, Event[]> = {
  'ai-governance': [{
    id: 'f6',
    time: '11:35',
    title: 'Panel 2: AI as the Backbone of the Future for a Human-centred Economy',
    type: 'PANEL',
    speakers: [{
      name: 'Maximo Ibarra',
      role: 'Former CEO, Engineering'
    }, {
      name: 'Alessandra Poggiani',
      role: 'General Director, Cineca'
    }, {
      name: 'Carlo Tortora Brayda di Belvedere',
      role: 'CEO, Cyber Eagle'
    }, {
      name: 'Giorgio Metta',
      role: 'Scientific Director, IIT'
    }],
    moderator: 'Camila Kakabadse'
  }, {
    id: 'f16',
    time: '17:15',
    title: 'Panel 8: The Ecosystems Where Innovation Happens',
    type: 'PANEL',
    speakers: [{
      name: 'Mehmet Naci Inci',
      role: 'Rector, Boğaziçi University'
    }, {
      name: 'Dina Storey',
      role: 'Executive Director, Expo 2030 Riyadh'
    }, {
      name: 'Muhannad Al Salhi',
      role: 'CEO, Engel & Völkers Italy'
    }, {
      name: 'Diego Raiteri',
      role: 'Vice-president, EIIS'
    }],
    moderator: 'Raquel Armendariz Sucunza'
  }],
  'climate-adaptation': [{
    id: 's2',
    time: '10:00',
    title: 'Panel 10: Cities, Regions and Universities as Living Laboratories for Climate Action (SDSN)',
    type: 'PANEL',
    speakers: [{
      name: 'Sorin Cheval',
      role: 'Professor, Babeș-Bolyai University Cluj-Napoca & University of Bucharest'
    }, {
      name: 'Amerigo Lombardi',
      role: 'Grant & Project Manager Officer, Icons Foundation'
    }, {
      name: 'Ione Avila-Palencia',
      role: 'Senior researcher, Technical Universidad Politécnica de Madrid'
    }],
    moderator: 'Andrija Erac — Head of Networks, Networks Program'
  }, {
    id: 'f12',
    time: '15:40',
    title: 'Panel 6: Rethinking Agrifood Systems',
    type: 'PANEL',
    speakers: [{
      name: 'Ngozi Ifeoma Odiaka',
      role: 'Country Director, UNSDSN Nigeria'
    }, {
      name: 'John Thwaites',
      role: 'Professor, Monash Business School'
    }, {
      name: 'Alain Rival',
      role: 'Professor & Senior Project Manager, CIRAD-UMR ABSys'
    }],
    moderator: 'Sveva Ciapparoni'
  }],
  'food-security': [{
    id: 'f11',
    time: '15:10',
    title: 'Panel 5 [ITA]: Feeding the Future of Food',
    type: 'PANEL',
    speakers: [{
      name: 'Laura Marchelli',
      role: 'Group Sustainability Manager, Mutti S.p.A.'
    }, {
      name: 'Giulia Castellucci',
      role: 'Public Affairs Manager, YARA Italia'
    }, {
      name: 'Andrea Campelli',
      role: 'Communications & External Relations Director, COREPLA'
    }, {
      name: 'Elena Sgaravatti',
      role: 'Vice President, Assobiotec-Federchimica'
    }],
    moderator: 'Stefania Iodice'
  }, {
    id: 'f13',
    time: '16:05',
    title: "Research: Rethinking Food Consumers' Choices,",
    type: 'RESEARCH',
    speakers: [{
      name: 'Speaker',
      role: 'Unicamillus',
      tbc: 'tbd'
    }, {
      name: 'Speaker',
      role: 'EIIS',
      tbc: 'tbd'
    }],
    moderator: ''
  }],
  biodiversity: [{
    id: 's6',
    time: '11:40',
    title: 'Panel 11: Converging Systems for Nature-Positive Economies',
    type: 'PANEL',
    speakers: [{
      name: 'Andreas Beckmann',
      role: 'Regional CEO, WWF Central and Eastern Europe'
    }, {
      name: 'Fabio Zardini',
      role: 'Country Manager, Patagonia Italy'
    }, {
      name: 'Andrea Cattaneo',
      role: 'Sustainability Impact Controller, H&M Group'
    }, {
      name: 'Chris Heemskerk',
      role: 'CEO & Founder, The Innovation Alliance'
    }, {
      name: 'Luca Ruini',
      role: 'VP Health, Safety, Environment & Energy, Barilla Group'
    }, {
      name: 'André Buldini',
      role: 'General Manager, Six Senses Rome'
    }],
    moderator: 'Petru Capatina'
  }, {
    id: 'f7',
    time: '12:05',
    title: 'Panel 3 [ITA]: Where Regenerative Industry Is Emerging – Part 1',
    type: 'PANEL',
    speakers: [{
      name: 'Mauro Fontana',
      role: 'President, Mutti S.p.A.'
    }, {
      name: 'Maura Latini',
      role: 'President, COOP'
    }, {
      name: 'Fabrizio Gavelli',
      role: 'President & General Manager, Ferrero Italy'
    }, {
      name: 'Lara Ponti',
      role: 'Vice President, Confindustria'
    }],
    moderator: 'Stefania Iodice'
  }],
  'innovative-finance': [{
    id: 'f14',
    time: '16:20',
    title: 'Panel 7: Financing the Great Transitions',
    type: 'PANEL',
    speakers: [{
      name: 'Francesco Tramontin',
      role: 'Vice President, Global Public Affairs, Ferrero',
      tbc: 'tbc'
    }, {
      name: 'Milena Messori',
      role: 'Head, EIB Group Office Italy'
    }, {
      name: 'Jyotsna Puri',
      role: 'Director, Policy & Programmes, UNEP'
    }, {
      name: 'Gustavo Osvaldo Beliz',
      role: 'Permanent Member, Pontifical Academy of Social Sciences'
    }, {
      name: 'David Donoghue',
      role: 'Former Irish Ambassador'
    }],
    moderator: 'Nardos Berehe'
  }, {
    id: 's3',
    time: '10:30',
    title: 'Panel 11: Financing the Future Locally: From FX Risk to Financial System Transformation',
    type: 'PANEL',
    speakers: [{
      name: 'Gulnara Yunusova',
      role: 'Treasurer, IFAD'
    }, {
      name: 'Ruurd Brower',
      role: 'CEO, The Currency Exchange Fund'
    }, {
      name: 'Philip Buyskes',
      role: 'CEO, Frontclear',
      tbc: 'tbc'
    }, {
      name: 'Karina Patrício Ferreira Lima',
      role: 'Senior Fellow, University of Leeds'
    }],
    moderator: 'Sean Lo Prinzi'
  }],
  'twin-transition': [{
    id: 'f5',
    time: '10:50',
    title: 'Panel 1: The Next Infrastructure Systems',
    type: 'PANEL',
    speakers: [{
      name: 'Gianbattista La Rocca',
      role: 'CEO, Italo S.p.A.'
    }, {
      name: 'Eugenie L. Birch',
      role: 'Co-Director, Penn Institute for Urban Research'
    }, {
      name: 'Nicoletta Russo',
      role: 'Head of IR & Sustainability, Ferrari'
    }, {
      name: 'Andrea Rocco',
      role: 'Chief Sustainability & Risk Officer, Brembo'
    }, {
      name: 'Marek Reichman',
      role: 'Executive Vice President & CCO, Aston Martin'
    }, {
      name: 'Patrick Zhong',
      role: 'Former Head of Global Investments, Fosun Group'
    }],
    moderator: 'Martina Schiuma',
    moderatorTbc: 'tbc'
  }, {
    id: 'f6',
    time: '11:35',
    title: 'Panel 2: AI as the Backbone of the Future for a Human-centred Economy',
    type: 'PANEL',
    speakers: [{
      name: 'Maximo Ibarra',
      role: 'Former CEO, Engineering'
    }, {
      name: 'Alessandra Poggiani',
      role: 'General Director, Cineca'
    }, {
      name: 'Carlo Tortora Brayda di Belvedere',
      role: 'CEO, Cyber Eagle'
    }, {
      name: 'Giorgio Metta',
      role: 'Scientific Director, IIT'
    }],
    moderator: 'Camila Kakabadse'
  }],
  'frontier-tech': [{
    id: 'f6',
    time: '11:35',
    title: 'Panel 2: AI as the Backbone of the Future for a Human-centred Economy',
    type: 'PANEL',
    speakers: [{
      name: 'Maximo Ibarra',
      role: 'Former CEO, Engineering'
    }, {
      name: 'Alessandra Poggiani',
      role: 'General Director, Cineca'
    }, {
      name: 'Carlo Tortora Brayda di Belvedere',
      role: 'CEO, Cyber Eagle'
    }, {
      name: 'Giorgio Metta',
      role: 'Scientific Director, IIT'
    }],
    moderator: 'Camila Kakabadse'
  }, {
    id: 'f18',
    time: '18:05',
    title: 'Panel 9: Moonshots for Development (IFAD)',
    type: 'PANEL',
    speakers: [{
      name: 'Christoph Aubrecht',
      role: 'Programme Coordinator, ESA'
    }, {
      name: 'Representative',
      role: 'IFAD',
      tbc: 'tbd'
    }, {
      name: 'Mara Balestrini',
      role: 'Strategy Advisor, LNET'
    }, {
      name: 'Lisa Finneran',
      role: 'World Bank',
      tbc: 'tbc'
    }, {
      name: 'Representative',
      role: 'EBRD',
      tbc: 'tbd'
    }, {
      name: 'Representative',
      role: 'Agropolis International',
      tbc: 'tbd'
    }],
    moderator: 'Sean Lo Prinzi'
  }],
  regenerative: [{
    id: 'f7',
    time: '12:05',
    title: 'Panel 3 [ITA]: Where Regenerative Industry Is Emerging – Part 1',
    type: 'PANEL',
    speakers: [{
      name: 'Mauro Fontana',
      role: 'President, Mutti S.p.A.'
    }, {
      name: 'Maura Latini',
      role: 'President, COOP'
    }, {
      name: 'Fabrizio Gavelli',
      role: 'President & General Manager, Ferrero Italy'
    }, {
      name: 'Lara Ponti',
      role: 'Vice President, Confindustria'
    }],
    moderator: 'Stefania Iodice'
  }, {
    id: 's6',
    time: '11:40',
    title: 'Panel 11: Converging Systems for Nature-Positive Economies',
    type: 'PANEL',
    speakers: [{
      name: 'Andreas Beckmann',
      role: 'Regional CEO, WWF Central and Eastern Europe'
    }, {
      name: 'Fabio Zardini',
      role: 'Country Manager, Patagonia Italy'
    }, {
      name: 'Andrea Cattaneo',
      role: 'Sustainability Impact Controller, H&M Group'
    }, {
      name: 'Chris Heemskerk',
      role: 'CEO & Founder, The Innovation Alliance'
    }, {
      name: 'Luca Ruini',
      role: 'VP Health, Safety, Environment & Energy, Barilla Group'
    }, {
      name: 'André Buldini',
      role: 'General Manager, Six Senses Rome'
    }],
    moderator: 'Petru Capatina'
  }],
  partnerships: [{
    id: 'f18',
    time: '18:05',
    title: 'Panel 9: Moonshots for Development (IFAD)',
    type: 'PANEL',
    speakers: [{
      name: 'Christoph Aubrecht',
      role: 'Programme Coordinator, ESA'
    }, {
      name: 'Representative',
      role: 'IFAD',
      tbc: 'tbd'
    }, {
      name: 'Mara Balestrini',
      role: 'Strategy Advisor, LNET'
    }, {
      name: 'Lisa Finneran',
      role: 'World Bank',
      tbc: 'tbc'
    }, {
      name: 'Representative',
      role: 'EBRD',
      tbc: 'tbd'
    }, {
      name: 'Representative',
      role: 'Agropolis International',
      tbc: 'tbd'
    }],
    moderator: 'Sean Lo Prinzi'
  }, {
    id: 'f17',
    time: '17:45',
    title: 'KEYNOTE: The Future of Global Partnerships',
    type: 'KEYNOTE',
    speakers: [{
      name: 'Juan Lucas Restrepo',
      role: 'Director General, Alliance of Bioversity International & CIAT'
    }]
  }],
  'skills-mobility': [{
    id: 'f10',
    time: '14:35',
    title: 'Panel 4: Signals of Next-Generation Leadership',
    type: 'PANEL',
    speakers: [{
      name: 'Daniela Fatarella',
      role: 'CEO, Save the Children Italy'
    }, {
      name: 'Patrizia Lombardi',
      role: 'Professor, Polytechnic University of Turin'
    }, {
      name: 'Phoebe Koundouri',
      role: 'Professor, Athens University & University of Cambridge'
    }, {
      name: 'Jacqueline Corbelli',
      role: 'CEO, BrightLine'
    }, {
      name: 'Laura De Dilectis',
      role: 'CEO VWH, Vice President DXS, Psychologist'
    }],
    moderator: 'Eugenio Russo'
  }, {
    id: 's9',
    time: '13:00',
    title: 'Panel 12: The Future Seen by the Next Generation',
    type: 'PANEL',
    speakers: [{
      name: 'Speaker',
      role: 'The Jackal',
      tbc: 'tbd'
    }, {
      name: 'Monica Lucarelli',
      role: 'Councillor for Productive Activities and Equal Opportunities, Municipality of Rome'
    }],
    moderator: 'Stefania Iodice'
  }]
};
interface Props {
  topicId: string;
  onBack: () => void;
}
export function TopicDetail({
  topicId,
  onBack
}: Props) {
  const {
    t
  } = useLanguage();
  const topic = useMemo(() => TOPICS.find(tp => tp.id === topicId), [topicId]);
  const profiles = useMemo(() => {
    if (!topic) return [];
    return MOCK_PROFILES.filter(p => p.topicIds.includes(topic.id)).sort((a, b) => b.matchScore - a.matchScore).slice(0, 8);
  }, [topic]);
  const [selectedProfile, setSelectedProfile] = useState<MockProfile | null>(null);
  const [projectModalProfile, setProjectModalProfile] = useState<MockProfile | null>(null);
  const [activeTab, setActiveTab] = useState<'profiles' | 'schedule'>('profiles');
  if (!topic) return null;
  const relatedEvents = TOPIC_RELATED_EVENTS[topic.id] || [];
  if (selectedProfile) {
    return <ProfileDetail profile={selectedProfile} onBack={() => setSelectedProfile(null)} data-id="element-981" />;
  }
  const imageUrl = TOPIC_IMAGES[topic.id] || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80';
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] overflow-y-auto" data-id="element-982">
      {/* Hero Image Header */}
      <div className="relative w-full h-[220px] shrink-0" data-id="element-983">
        <img src={imageUrl} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" data-id="element-984" />
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(to bottom, transparent 30%, #0D0D0D 100%)'
      }} data-id="element-985" />

        {/* Back button */}
        <button onClick={onBack} aria-label={t('common.back')} className="absolute top-5 left-5 z-20 w-10 h-10 rounded-full bg-[#0D0D0D]/40 backdrop-blur-md flex items-center justify-center text-white/80 hover:text-white transition-colors" data-id="element-986">
          <ChevronLeft className="w-6 h-6 -ml-0.5" strokeWidth={2} data-id="element-987" />
        </button>

        {/* Title overlaid at bottom */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-4 pl-[10px] pr-[10px]" data-id="element-988">
          <h1 className="text-white drop-shadow-sm text-[22px] font-[500] pl-[10px] pr-[10px]" data-id="element-989">
            {t(topic.nameKey)}
          </h1>
        </div>
      </div>

      <div className="px-6 pt-4 pb-24 pl-[20px] pr-[20px]" data-id="element-990">
        <div className="animate-in fade-in duration-300 space-y-4" data-id="element-991">
          {profiles.map((profile, index) => <div key={profile.id} onClick={() => setProjectModalProfile(profile)} className="bg-[#141414] border border-[#2A2A2A] rounded-[14px] p-3 cursor-pointer relative overflow-hidden" data-id="element-992">
              <div className="flex items-center gap-3" data-id="element-993">
                <div className="w-10 h-10 rounded-full bg-[#2A2A2A] shrink-0 overflow-hidden relative" data-id="element-994">
                  <img src={`https://randomuser.me/api/portraits/${parseInt(profile.id.replace('p', ''), 10) % 2 === 0 ? 'women' : 'men'}/${parseInt(profile.id.replace('p', ''), 10)}.jpg`} alt="" className="w-full h-full object-cover scale-110" style={{
                filter: 'blur(3px)'
              }} data-id="element-995" />
                  <div className="absolute inset-0 bg-black/10 rounded-full" data-id="element-996" />
                </div>

                <div className="flex-1 min-w-0" data-id="element-997">
                  <div className="text-white text-[13px] font-medium truncate" data-id="element-998">
                    {profile.role}
                  </div>
                  <div className="text-[#888888] text-[11px] truncate" data-id="element-999">
                    {profile.sector}
                  </div>
                  <button onClick={e => {
                e.stopPropagation();
                setProjectModalProfile(profile);
              }} className="text-[#C5D82D] text-[11px] font-medium mt-0.5 hover:underline" data-id="element-1000">
                    Dettagli →
                  </button>
                </div>

                <div className="shrink-0" data-id="element-1001">
                  <button onClick={e => {
                e.stopPropagation();
                setSelectedProfile(profile);
              }} className="px-3 py-1 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[11px] font-semibold hover:bg-[#d4e63a] transition-colors" data-id="element-1002">
                    Incontra
                  </button>
                </div>
              </div>
            </div>)}
          {profiles.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-1003">
              <User className="w-8 h-8 text-[#444444] mb-3" data-id="element-1004" />
              <p className="text-[14px] text-[#888888]" data-id="element-1005">
                Nessun profilo suggerito per questo tema.
              </p>
            </div>}
        </div>
      </div>

      {/* Projects Modal */}
      {projectModalProfile && <div className="fixed inset-0 z-50 flex flex-col justify-end" data-id="element-1006">
          <div className="absolute inset-0 bg-black/70 animate-in fade-in duration-200" onClick={() => setProjectModalProfile(null)} data-id="element-1007" />
          <div className="relative bg-[#141414] rounded-t-2xl border-t border-[#2A2A2A] max-h-[93%] min-h-[85%] flex flex-col" style={{
        animation: 'drawerSlideUp 280ms cubic-bezier(0.16, 1, 0.3, 1) both'
      }} data-id="element-1008">
            {/* Fixed header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 shrink-0" data-id="element-1009">
              <h2 className="text-[16px] text-white font-semibold" data-id="element-1010">Profilo</h2>
              <button onClick={() => setProjectModalProfile(null)} className="w-8 h-8 bg-[#1A1A1A] rounded-full flex items-center justify-center text-[#888888] border border-[#2A2A2A]" data-id="element-1011">
                <X className="w-5 h-5" data-id="element-1012" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 pb-8 min-h-0" data-id="element-1013">
              {/* Profile header */}
              <div className="flex items-center gap-3 mb-5" data-id="element-1014">
                <div className="w-12 h-12 rounded-full bg-[#2A2A2A] shrink-0 overflow-hidden relative" data-id="element-1015">
                  <img src={`https://randomuser.me/api/portraits/${parseInt(projectModalProfile.id.replace('p', ''), 10) % 2 === 0 ? 'women' : 'men'}/${parseInt(projectModalProfile.id.replace('p', ''), 10)}.jpg`} alt="" className="w-full h-full object-cover scale-110" style={{
                filter: 'blur(3px)'
              }} data-id="element-1016" />
                  <div className="absolute inset-0 bg-black/10 rounded-full" data-id="element-1017" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1018">
                  <div className="text-[#C5D82D] text-[13px] font-medium truncate" data-id="element-1019">
                    {projectModalProfile.role}
                  </div>
                  <div className="text-[#888888] text-[12px] truncate" data-id="element-1020">
                    {projectModalProfile.sector}
                  </div>
                </div>
                <div className="px-2 py-1 rounded-full border border-[#C5D82D]/40 text-[#C5D82D] text-[11px] font-semibold whitespace-nowrap bg-[#000000]" data-id="element-1021">
                  {projectModalProfile.matchScore}% Match
                </div>
              </div>

              {/* Description */}
              <div className="mb-5" data-id="element-1022">
                <h3 className="text-[11px] text-white uppercase tracking-wider font-semibold mb-2" data-id="element-1023">
                  Descrizione
                </h3>
                <p className="text-[#A0A0A0] text-[13px] leading-relaxed" data-id="element-1024">
                  Lavora su iniziative strategiche nel settore{' '}
                  {projectModalProfile.sector.toLowerCase()}, con un focus
                  particolare sull'innovazione e l'impatto a lungo termine.
                </p>
              </div>

              {/* Topics */}
              <div data-id="element-1025">
                <h3 className="text-[11px] text-white uppercase tracking-wider font-semibold mb-2" data-id="element-1026">
                  Temi preferiti
                </h3>
                <div className="flex flex-wrap gap-1.5" data-id="element-1027">
                  {TOPICS.filter(tp => projectModalProfile.topicIds.includes(tp.id)).map(tp => <span key={tp.id} className="px-2.5 py-1 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] text-white text-[11px]" data-id="element-1028">
                      {t(tp.nameKey)}
                    </span>)}
                </div>
              </div>
            </div>
            {/* end scrollable content */}
          </div>
          <style dangerouslySetInnerHTML={{
        __html: `
              @keyframes drawerSlideUp {
                from { transform: translateY(100%); opacity: 0; }
                to { transform: none; opacity: 1; }
              }
            `
      }} data-id="element-1029" />
        </div>}
    </div>;
}